import React, { useState, useMemo, useEffect } from "react";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";

import { ExternalButton, InternalButton } from "@sharedComponents/buttons";
import { TextAreaField } from "@stadium/ui/src/components/input/bs-input";

import SizeChartModal from "@components/modals/size-chart";
import SwagOptionsSelector from "@components/store/product-detail/shared/swag-options-selector";
import ProductOptionSelector from "@components/store/product-detail/shared/product-option-selector";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_SELECTED } from "@stadium/services/analytics/constants/default";
import { useModal } from "@stadium/hooks/lib/use-modal";

import { useProductDetailContext } from "@contexts/product-detail-context";

import {
  getMasterVariant,
  getProductOptionTypesData,
  getProductVariantsData,
  processVariantImages,
  getDefaultIndexIfSingleVariantAvailable,
  sortInvalidColorLast,
} from "@helpers/product-details";

import { SIZE_CHART_MODAL } from "@constants/modal-types";
import { ANALYTICS_TARGET_ATTRIBUTE, ANALYTICS_ATTRIBUTE_TYPE } from "@constants/analytics/shop";

import NoImage from "@assets/images/no-image.svg";

import styles from "./main.module.scss";

const VariantSelector = ({
  product = {},
  setSelectedVariantsData,
  showError,
  productId,
  showPreferencesInput = false,
}) => {
  const masterVariant = useMemo(() => getMasterVariant(product), [product]);

  const [optionTypeVariantMapping, setOptionTypeVariantMapping] = useState({});
  const [variantSelected, setVariantSelected] = useState({});
  const [selectedSizeVariant, setSelectedSizeVariant] = useState({});
  const [allSizesVariants, setAllSizesVariants] = useState([]);
  const [preferences, setPreferences] = useState("");

  const {
    setIsOptionSelected,
    setIsColorSelected,
    setOptionSelectionError,
    isColorSelected,
    isOptionSelected,
    setColorSelectionError,
  } = useProductDetailContext();

  const withAnalytics = useWithAnalytics();

  const {
    name = "",
    sizeChartEnabled,
    sizeChart,
    intent,
    sizeChartLink,
    askForPreference,
  } = product;

  const optionTypesData = useMemo(() => getProductOptionTypesData(product), [product]);

  const getDefaultSelectedVariantIndex = (variants, variantsData) => {
    if (variants.length === 1) {
      // If product has only single variant
      return 0;
    } else {
      return getDefaultIndexIfSingleVariantAvailable(variants, variantsData);
    }
  };

  const setDefaultSelectedVariant = (variant) => {
    if (variant) {
      let defaultSecondaryVariant;
      const sizesAvailable = (variant.variants || []).filter(({ outOfStock }) => !outOfStock);
      if (sizesAvailable.length === 1) defaultSecondaryVariant = sizesAvailable[0];
      if (defaultSecondaryVariant) {
        setVariantSelected(defaultSecondaryVariant);
        setSelectedSizeVariant(defaultSecondaryVariant);
        setIsOptionSelected(true);
      }
      setOptionTypeVariantMapping(variant);
      setIsColorSelected(true);
    }
  };

  const variantsData = useMemo(() => getProductVariantsData(optionTypesData), [optionTypesData]);

  const setAllOrderedSizeVariants = (products) => {
    setAllSizesVariants(sortBy(products, "position"));
  };

  useEffect(() => {
    if (isEmpty(allSizesVariants) && optionTypesData.size && optionTypesData.color)
      setAllOrderedSizeVariants(
        uniqBy(optionTypesData.size, ({ id }) => id).map((item) => ({
          ...item,
          outOfStock: false,
        })),
      );
  }, [allSizesVariants, optionTypesData]);

  useEffect(() => {
    const variants = Object.keys(variantsData);

    if (!isEmpty(variants)) {
      const defaultSelected = getDefaultSelectedVariantIndex(variants, variantsData);
      setDefaultSelectedVariant(variantsData[variants[defaultSelected]]);
    } else if (masterVariant) {
      setDefaultSelectedVariant({
        ...masterVariant,
        images: processVariantImages(masterVariant.images),
      });
    }
  }, [variantsData, optionTypesData, masterVariant]);

  useEffect(() => {
    if (!isEmpty(allSizesVariants) && !isEmpty(optionTypeVariantMapping?.variants)) {
      // upadate sizeVariants as per current selected color
      // if no variant is there assume it as OOS
      const updatedSizeVariants = uniqBy(
        [
          ...optionTypeVariantMapping.variants,
          ...allSizesVariants.map((item) => ({
            ...item,
            outOfStock: true,
          })),
        ],
        ({ id }) => id,
      );

      setAllOrderedSizeVariants(updatedSizeVariants);
      if (!isEmpty(selectedSizeVariant)) {
        // resetting size variant value accroding to updated optionType
        setSelectedSizeVariant(updatedSizeVariants.find(({ id }) => id === selectedSizeVariant.id));
      }
    }
  }, [optionTypeVariantMapping]);

  useEffect(() => {
    let sizeVariantForCurrentOptionType = {};
    if (optionTypeVariantMapping?.variants && !isEmpty(selectedSizeVariant)) {
      sizeVariantForCurrentOptionType =
        optionTypeVariantMapping.variants.find(({ id }) => id === selectedSizeVariant.id) || {};
    }
    setVariantSelected(sizeVariantForCurrentOptionType);

    if (!isEmpty(allSizesVariants)) {
      isEmpty(selectedSizeVariant) ? setIsOptionSelected(false) : setIsOptionSelected(true);
    }
  }, [selectedSizeVariant]);

  const isColorPresent = Boolean(optionTypesData.color);
  const hasSizeOptions = Boolean(optionTypesData.size);

  const { openModal: openSizeChartModal, renderModal: renderSizeChartModal } = useModal(
    SIZE_CHART_MODAL,
    SizeChartModal,
  );

  const variantValues = useMemo(
    () => Object.values(variantsData).sort(sortInvalidColorLast),
    [variantsData],
  );

  useEffect(() => {
    // if showError is true then we are updating color and size state error true so it reflect on FE
    if (showError) {
      if (!isColorSelected) {
        setColorSelectionError(true);
      }

      if (hasSizeOptions && !isOptionSelected) {
        setOptionSelectionError(true);
      }
    }
  }, [showError, isColorSelected, isOptionSelected]);

  useEffect(() => {
    if (!isColorPresent || (variantValues.length < 2 && !isColorSelected)) {
      setIsColorSelected(true);
    }
  }, [isColorPresent, variantValues]);

  useEffect(() => {
    const selectedProduct =
      isColorPresent && hasSizeOptions ? variantSelected : optionTypeVariantMapping;

    const isErrorPresent = !isColorSelected || (hasSizeOptions && !isOptionSelected);

    setSelectedVariantsData((variants) => ({
      ...variants,
      [productId]: { ...selectedProduct, error: isErrorPresent, dietaryPreference: preferences },
    }));
  }, [variantSelected, optionTypeVariantMapping, isOptionSelected, isColorSelected, preferences]);

  const renderColorVariants = () => {
    if (!isColorPresent || variantValues.length < 2) {
      return null;
    }
    const selectedColor = optionTypeVariantMapping?.presentation;
    const title = selectedColor ? `Color: ${selectedColor}` : "Choose color";

    return (
      <SwagOptionsSelector
        title={title}
        availData={variantValues}
        variantSelected={selectedSizeVariant}
        selectedData={optionTypeVariantMapping}
        disabledIfOutOfStock={!hasSizeOptions}
        handleChange={(data) => {
          setSelectedSizeVariant({});
          setOptionTypeVariantMapping(data);

          withAnalytics(undefined, {
            target: ANALYTICS_TARGET_ATTRIBUTE,
            action: ANALYTICS_EVENT_ACTION_SELECTED,
            track: true,
            eventProperties: {
              product_id: productId,
              attribute_type: ANALYTICS_ATTRIBUTE_TYPE.COLOR,
              attribute_value: data.presentation,
            },
          })();
        }}
        productId={productId}
        errorClassName={styles.errorColorText}
      />
    );
  };

  const getSizeProps = () => {
    const variantValues = Object.values(variantsData);
    if (isColorPresent)
      return {
        availData: allSizesVariants,
        selectedData: selectedSizeVariant,
        handleChange: (data) => {
          setSelectedSizeVariant(data);
          withAnalytics(undefined, {
            target: ANALYTICS_TARGET_ATTRIBUTE,
            action: ANALYTICS_EVENT_ACTION_SELECTED,
            track: true,
            eventProperties: {
              product_id: productId,
              attribute_type: ANALYTICS_ATTRIBUTE_TYPE.SIZE,
              attribute_value: data.presentation,
            },
          })();
        },
      };
    return {
      availData: variantValues,
      selectedData: optionTypeVariantMapping,
      handleChange: (data) => {
        setOptionTypeVariantMapping(data);
        withAnalytics(undefined, {
          target: ANALYTICS_TARGET_ATTRIBUTE,
          action: ANALYTICS_EVENT_ACTION_SELECTED,
          track: true,
          eventProperties: {
            product_id: productId,
            attribute_type: ANALYTICS_ATTRIBUTE_TYPE.SIZE,
            attribute_value: data.presentation,
          },
        })();
      },
    };
  };

  const renderSizeVariants = () => {
    if (!hasSizeOptions) {
      return null;
    }
    const sizeProps = getSizeProps();
    return (
      <ProductOptionSelector
        title="Size"
        intent={intent}
        {...sizeProps}
        errorClassName={styles.errorDdText}
      />
    );
  };

  const renderSizeChartCTA = () => {
    if (sizeChartEnabled) {
      return (
        <InternalButton onClick={openSizeChartModal} className={styles.sizeGuide} styleType="link">
          Size Guide
        </InternalButton>
      );
    }
    if (sizeChartLink) {
      return (
        <ExternalButton
          styleType="link"
          href={sizeChartLink}
          className={styles.sizeGuide}
          target="_blank"
        >
          Size Guide
        </ExternalButton>
      );
    }
    return null;
  };

  const renderSizeAndQuantityOptions = () => (
    <div className={styles.sizeOptionWrap}>
      {renderSizeVariants()}
      {renderSizeChartCTA()}
    </div>
  );

  const renderPreferencesTextbox = () => {
    if (!askForPreference || !showPreferencesInput) return null;
    return (
      <div>
        <div className={styles.title}>Preferences</div>
        <TextAreaField
          id="preferences"
          value={preferences}
          placeholder="Type your answer here"
          onChange={setPreferences}
          className={styles.textAreaFormControl}
        />
      </div>
    );
  };

  const getSliderImages = () => {
    const variantIds = Object.keys(variantsData);
    if (!isEmpty(variantSelected)) return variantSelected.images;
    if (!isEmpty(optionTypeVariantMapping)) return optionTypeVariantMapping.images;
    // select first variant image if nothing is selected
    if (!isEmpty(variantsData?.[variantIds[0]]?.images)) return variantsData[variantIds[0]]?.images;
    if (!isEmpty(masterVariant?.images)) return processVariantImages(masterVariant.images);
    if (!isEmpty(product.variantImages)) return processVariantImages(product.variantImages);
    return [];
  };
  const sliderImages = useMemo(
    () => getSliderImages(),
    [variantSelected, optionTypeVariantMapping, variantsData],
  );

  const imgSrc = (!isEmpty(sliderImages) && sliderImages[0]?.image) || NoImage;

  return (
    <div className={styles.variantSelector}>
      <img
        src={imgSrc}
        alt="product-img"
        height={180}
        width={180}
        className={styles.variantImage}
      />
      <div className={styles.variantDetails}>
        <div className={styles.productName}>{name}</div>
        {renderColorVariants()}
        {renderSizeAndQuantityOptions()}
        {renderPreferencesTextbox()}
        {sizeChartEnabled &&
          renderSizeChartModal(
            {
              data: { productName: name, sizeChart, sizeChartLink },
            },
            false,
          )}
      </div>
    </div>
  );
};

export default VariantSelector;
